import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import {
  Button,
  DatePicker,
  Form,
  type FormListFieldData,
  type TableColumnsType
} from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import type { Dayjs } from "dayjs"
import React from "react"
import type { TFunction } from "i18next"

import type { useDateFormat } from "../../../../../../hooks/use-date-format.ts"
import InputNumber from "../../../../../../components/Input/InputNumber.tsx"
import { formListAddFocus } from "../../../../../../utils/form/form-list-add-focus.ts"
import { validateNumber } from "../../../../../../utils/validation/number.ts"
import {
  type ScheduleTriple,
  withDuration,
  withEndDate,
  withStartDate
} from "../../../../shared/duration-date-calculator.ts"
import type {
  TemplateDocumentFormData,
  TemplateDocumentScopeSectionPhase
} from "../../../../types/template-document-form-data.tsx"
import { DurationFormItem } from "../../DurationFormItem.tsx"
import { TextInput } from "../../../../../../components/Input/TextInput.tsx"

type FieldType = TemplateDocumentFormData["phases"]
type Phase = TemplateDocumentScopeSectionPhase
type DateFormatHelpers = ReturnType<typeof useDateFormat>

interface GetColumnsArg {
  form: FormInstance<TemplateDocumentFormData>
  hiddenColumns: string[]
  disabled?: boolean
  onRemove: (name: number) => void
  t: TFunction
  getDateValueProps: DateFormatHelpers["getDateValueProps"]
  normalizeDate: DateFormatHelpers["normalizeDate"]
}

const scheduleOf = (phase?: Partial<Phase> | null): ScheduleTriple => ({
  startDate: phase?.start_date ?? null,
  endDate: phase?.end_date ?? null,
  duration: phase?.duration_days ?? null,
  durationUnit: phase?.duration_type ?? null
})

export const getDocumentScopeSectionColumnsColumns = ({
  form,
  hiddenColumns,
  disabled,
  onRemove,
  t,
  getDateValueProps,
  normalizeDate
}: GetColumnsArg): TableColumnsType<FormListFieldData> => {
  /**
   * Each milestone row's own Start/End/Duration trio derives independently
   * (never the whole table at once) — one re-entrancy flag per row, created
   * fresh whenever this factory recomputes, so a `form.setFields` call made
   * by one row's derivation can never be mistaken for a user edit on another
   * row.
   */
  const updatingRows = new Set<number>()

  const runRowUpdate = (rowKey: number, apply: () => void) => {
    updatingRows.add(rowKey)
    apply()
    setTimeout(() => updatingRows.delete(rowKey), 0)
  }

  const phaseAt = (rowKey: number): Partial<Phase> | undefined =>
    form.getFieldValue(["phases", rowKey])

  const handleRowStartDateChange = (rowKey: number, value: Dayjs | null) => {
    if (updatingRows.has(rowKey)) return

    const derived = withStartDate(
      scheduleOf(phaseAt(rowKey)),
      normalizeDate(value)
    )
    if (derived.duration == null) return

    runRowUpdate(rowKey, () => {
      form.setFields([
        { name: ["phases", rowKey, "duration_days"], value: derived.duration },
        {
          name: ["phases", rowKey, "duration_type"],
          value: derived.durationUnit ?? "days"
        }
      ])
    })
  }

  const handleRowEndDateChange = (rowKey: number, value: Dayjs | null) => {
    if (updatingRows.has(rowKey)) return

    const derived = withEndDate(
      scheduleOf(phaseAt(rowKey)),
      normalizeDate(value)
    )
    if (derived.duration == null) return

    runRowUpdate(rowKey, () => {
      form.setFields([
        { name: ["phases", rowKey, "duration_days"], value: derived.duration },
        {
          name: ["phases", rowKey, "duration_type"],
          value: derived.durationUnit ?? "days"
        }
      ])
    })
  }

  const applyRowDerivedDuration = (
    rowKey: number,
    duration: number | null,
    durationUnit: string | null | undefined
  ) => {
    const schedule = scheduleOf(phaseAt(rowKey))
    const derived = withDuration(schedule, duration, durationUnit)

    if ((derived.endDate ?? null) !== (schedule.endDate ?? null)) {
      runRowUpdate(rowKey, () => {
        form.setFields([
          {
            name: ["phases", rowKey, "end_date"],
            value: derived.endDate ?? null
          }
        ])
      })
      return
    }

    if ((derived.startDate ?? null) !== (schedule.startDate ?? null)) {
      runRowUpdate(rowKey, () => {
        form.setFields([
          {
            name: ["phases", rowKey, "start_date"],
            value: derived.startDate ?? null
          }
        ])
      })
    }
  }

  const handleRowDurationValueChange = (
    rowKey: number,
    value: number | null
  ) => {
    if (updatingRows.has(rowKey)) return
    applyRowDerivedDuration(rowKey, value, phaseAt(rowKey)?.duration_type)
  }

  const handleRowDurationUnitChange = (rowKey: number, unit: string) => {
    if (updatingRows.has(rowKey)) return

    const rawDuration = phaseAt(rowKey)?.duration_days
    const duration = rawDuration == null ? null : Number(rawDuration)
    if (duration == null || !Number.isFinite(duration)) return

    applyRowDerivedDuration(rowKey, duration, unit)
  }

  return [
    {
      hidden: hiddenColumns?.includes("name"),
      title: "Scope Of Work",
      dataIndex: "name",
      key: "name",
      render: (_, field) => (
        <div className="space-y-2">
          <Form.Item<FieldType>
            name={[field.name, "name"]}
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <TextInput disabled={disabled} placeholder="Enter name" />
          </Form.Item>

          <Form.List name={[field.name, "checklist_items"]}>
            {(checklistFields, { add: addItem, remove: removeItem }) => (
              <div className="space-y-1">
                {checklistFields.map((checklistField) => (
                  <div
                    key={checklistField.key}
                    className="flex gap-1 items-center"
                  >
                    <span>•</span>
                    <Form.Item<
                      TemplateDocumentScopeSectionPhase["checklist_items"]
                    >
                      name={[checklistField.name]}
                      style={{ margin: 0, flex: 1 }}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Name is required"
                        }
                      ]}
                    >
                      <TextInput
                        disabled={disabled}
                        placeholder="Checklist item"
                        size="small"
                      />
                    </Form.Item>
                    <Button
                      disabled={disabled}
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeItem(checklistField.name)}
                    />
                  </div>
                ))}
                <Button
                  disabled={disabled}
                  type="dashed"
                  size="small"
                  onClick={() => {
                    formListAddFocus<TemplateDocumentFormData>(
                      () => addItem(""),
                      checklistFields,
                      form,
                      ["phases", field.name, "checklist_items"]
                    )
                  }}
                  icon={<PlusOutlined />}
                  className="w-full"
                >
                  Item
                </Button>
              </div>
            )}
          </Form.List>
        </div>
      )
    },
    {
      hidden: hiddenColumns?.includes("start_date"),
      title: t("documentTemplates.scopeOfWork.columns.targetStart") as string,
      width: 170,
      dataIndex: "start_date",
      key: "start_date",
      render: (_, field) => (
        <Form.Item<FieldType>
          name={[field.name, "start_date"]}
          style={{ margin: 0 }}
          getValueProps={getDateValueProps}
        >
          <DatePicker
            disabled={disabled}
            className="w-full"
            onChange={(value) => handleRowStartDateChange(field.name, value)}
          />
        </Form.Item>
      )
    },
    {
      hidden: hiddenColumns?.includes("end_date"),
      title: t("documentTemplates.scopeOfWork.columns.targetEnd") as string,
      width: 170,
      dataIndex: "end_date",
      key: "end_date",
      render: (_, field) => (
        <Form.Item<FieldType>
          name={[field.name, "end_date"]}
          style={{ margin: 0 }}
          getValueProps={getDateValueProps}
        >
          <DatePicker
            disabled={disabled}
            className="w-full"
            onChange={(value) => handleRowEndDateChange(field.name, value)}
          />
        </Form.Item>
      )
    },
    {
      hidden: hiddenColumns?.includes("duration"),
      title: "Duration",
      width: 255,
      dataIndex: "duration_days",
      key: "duration_days",
      render: (_, field) => (
        <DurationFormItem<FieldType>
          disabled={disabled}
          durationName={[field.name, "duration_days"]}
          typeName={[field.name, "duration_type"]}
          formItemProps={{ className: "mb-0" }}
          onValueChange={(value) =>
            handleRowDurationValueChange(field.name, value)
          }
          onUnitChange={(unit) => handleRowDurationUnitChange(field.name, unit)}
        />
      )
    },
    {
      hidden: hiddenColumns?.includes("budget"),
      title: "Cost",
      width: 225,
      dataIndex: "budget",
      key: "budget",
      render: (_, field) => (
        <Form.Item<FieldType>
          name={[field.name, "budget"]}
          style={{ margin: 0 }}
          rules={[{ validator: validateNumber }]}
        >
          <InputNumber
            disabled={disabled}
            stringMode
            className={"w-40"}
            min={0}
            placeholder="0"
            max={99999999.99} // 8 digits before decimal
            precision={2}
            suffix={"$"}
          />
        </Form.Item>
      )
    },
    {
      title: "",
      width: 50,
      render: (_, field) => (
        <Button
          disabled={disabled}
          type="text"
          danger
          size={"large"}
          icon={<DeleteOutlined />}
          onClick={() => onRemove(field.name)}
        />
      )
    }
  ]
}
